function buildAdvertDateRangeError(opening, closing) {
  if (!opening || !closing) {
    return "Please enter valid opening and closing date & time values.";
  }
  return closing <= opening
    ? "Closing date & time must be later than opening date & time."
    : "";
}

function buildAdvertSummary(advert) {
  return {
    id: advert.id,
    title: advert.title || "",
    reference_number: advert.reference_number || "",
    details: advert.details || "",
  };
}

function buildDisplayFallbackSections(sections = []) {
  return sections.map((_, index) => ({ key: `section:${index}`, source: "section", index }));
}

function buildEditDisplayFallback(hasDetails, sections) {
  return hasDetails
    ? [{ key: "details", source: "details" }, ...buildDisplayFallbackSections(sections || [])]
    : buildDisplayFallbackSections(sections || []);
}

const ADVERT_UPDATE_OPTIONAL_FIELDS = [
  "reference_number",
  "published_date",
  "closing_date",
  "details",
  "vacancies",
];

function normalizeAdvertUpdateTitle(editAdvertData) {
  return String(editAdvertData.title || "").trim();
}

function getAdvertUpdateOptionalValue(editAdvertData, field) {
  return Reflect.get(editAdvertData, field) || null;
}

function buildAdvertUpdateBase(editAdvertData) {
  const optional_entries = ADVERT_UPDATE_OPTIONAL_FIELDS.map((field) => [
    field,
    getAdvertUpdateOptionalValue(editAdvertData, field),
  ]);
  return Object.fromEntries([["title", normalizeAdvertUpdateTitle(editAdvertData)], ...optional_entries]);
}

export default {
  methods: {
    parseAdvertDateTime(value) {
      if (!value) return null;
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    },
    validateAdvertDateRange(openingDate, closingDate) {
      if (!openingDate || !closingDate) return true;
      const opening = this.parseAdvertDateTime(openingDate);
      const closing = this.parseAdvertDateTime(closingDate);
      const error_message = buildAdvertDateRangeError(opening, closing);
      if (!error_message) return true;
      this.$emit("advert-error", error_message);
      return false;
    },
    buildEditDisplayFallback() {
      return buildEditDisplayFallback(this.advertHasDetails(this.editAdvertData), this.editAdvertData.sections);
    },
    rebuildEditDisplayOrder() {
      const current = Array.isArray(this.editAdvertData.display_order) ? this.editAdvertData.display_order : [];
      const fallback = this.buildEditDisplayFallback();
      this.editAdvertData.display_order = this.normalizeDisplayOrder(
        current,
        this.advertHasDetails(this.editAdvertData),
        fallback.filter((item) => item.source === "section")
      );
    },
    hasValidDisplayDragTarget(sourceIndex, targetIndex) {
      return sourceIndex !== null && sourceIndex !== undefined && sourceIndex !== targetIndex;
    },
    buildEditSectionsPayload() {
      return [
        ...(this.editAdvertData.sections || []),
        { type: "__display_order_meta__", items: this.editAdvertData.display_order || [] },
      ];
    },
    buildUpdateAdvertFields() {
      return buildAdvertUpdateBase(this.editAdvertData);
    },
    buildUpdateAdvertPayload() {
      return {
        advert_id: this.editAdvertData.id,
        ...this.buildUpdateAdvertFields(),
        sections: this.buildEditSectionsPayload(),
      };
    },
    hasAdvertTitle() {
      return !!(this.editAdvertData.title && String(this.editAdvertData.title).trim());
    },
    hasAdvertReferenceNumber() {
      return !!(this.editAdvertData.reference_number && String(this.editAdvertData.reference_number).trim());
    },
    validateAdvertInputs() {
      if (!this.hasAdvertTitle()) {
        this.$emit("advert-error", "Advert title is required.");
        return false;
      }
      if (!this.hasAdvertReferenceNumber()) {
        this.$emit("advert-error", "Reference number is required.");
        return false;
      }
      return this.validateAdvertDateRange(
        this.editAdvertData.published_date,
        this.editAdvertData.closing_date
      );
    },
    buildMergedUpdatedAdvert() {
      return {
        ...this.selectedAdvert,
        ...this.editAdvertData,
        sections: this.buildEditSectionsPayload(),
      };
    },
    buildUpdatedAdvertSummary(mergedAdvert) {
      return {
        ...buildAdvertSummary(mergedAdvert),
        published_date: mergedAdvert.published_date || "",
        closing_date: mergedAdvert.closing_date || "",
        vacancies: mergedAdvert.vacancies || null,
      };
    },
    applyUpdatedAdvertViewData(mergedAdvert) {
      this.viewAdvertData = {
        ...this.viewAdvertData,
        ...this.buildUpdatedAdvertSummary(mergedAdvert),
        sections: this.editAdvertData.sections || [],
        display_order: this.editAdvertData.display_order || [],
      };
      this.viewAdvertData.display_blocks = this.buildDisplayBlocks(this.viewAdvertData);
      this.editMode = false;
    },
    handleAdvertUpdateSuccess(response) {
      if (response.data?.status !== "success") {
        const message = response.data?.message || "Unexpected response";
        this.show_error(message);
        this.$emit("advert-error", message);
        return false;
      }
      this.applyUpdatedAdvertViewData(this.buildMergedUpdatedAdvert());
      this.$emit("advert-updated");
      return true;
    },
    emitAdvertSaveError(error) {
      const message = error.response?.data?.message || error.message || "Failed to update advert.";
      this.show_error(message);
      this.$emit("advert-error", message);
    },
  },
};
